/** @jsx jsx */

import { Fragment, forwardRef } from 'react';
import { VisuallyHidden } from '@balance-web/a11y';
import { jsx } from '@balance-web/core';
import { useMaybeFieldContext } from '@balance-web/field';
import {
  FOCUS_RING,
  FORM_CONTROL_SURFACE_DISABLED,
  useTheme,
} from '@balance-web/theme';

import type { RadioPrimitiveProps, RadioSize } from './types';

export const RadioPrimitive = forwardRef<HTMLInputElement, RadioPrimitiveProps>(
  ({ invalid: invalidProp, size, ...inputProps }, ref) => {
    const fieldContext = useMaybeFieldContext();
    const isInvalid = invalidProp ?? fieldContext?.invalid;

    return (
      <Fragment>
        <VisuallyHidden
          ref={ref}
          as="input"
          type="radio"
          {...inputProps}
          aria-checked={inputProps.checked}
          aria-invalid={isInvalid}
          data-invalid={isInvalid}
        />
        <Indicator size={size}>
          <DotIcon size={size} />
        </Indicator>
      </Fragment>
    );
  }
);

const sizeToScaleKey = {
  small: 'xxsmall',
  medium: 'xsmall',
} as const;

const Indicator = ({
  size = 'medium',
  ...props
}: {
  children?: React.ReactNode;
  size?: RadioSize;
}) => {
  const { semantic, sizing } = useTheme();
  const resolvedSize = sizing[sizeToScaleKey[size]];

  return (
    <div
      aria-hidden="true"
      css={{
        alignItems: 'center',
        backgroundColor: semantic.colour.neutral.background,
        border: `2px solid ${semantic.colour.neutral.border}`,
        borderRadius: '50%',
        boxSizing: 'border-box',
        color: semantic.colour.neutral.background,
        cursor: 'pointer',
        display: 'flex',
        flexShrink: 0,
        height: resolvedSize,
        justifyContent: 'center',
        width: resolvedSize,
        position: 'relative',

        // For the outter circle on hover
        '::after': {
          content: '""',
          display: 'block',
          width: '180%',
          height: '180%',
          borderRadius: 9999,
          background: '#efefef',
          opacity: 0,
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%,-50%)',
          zIndex: -1,
        },

        'input:not([disabled]):input:hover + &': {
          '::after': {
            opacity: 0.7,
          },
        },

        'input:not([disabled]):focus + &': {
          boxShadow: `0 0 0 2px ${FOCUS_RING}`,
        },

        'input + & svg': {
          opacity: 0,
        },
        'input:checked + & svg': {
          opacity: 1,
        },

        'input:checked + &': {
          backgroundColor: semantic.colour.neutral.background,
          borderColor: semantic.colour.primary.background,
          color: semantic.colour.primary.background,
        },
        'input:disabled + &': {
          backgroundColor: FORM_CONTROL_SURFACE_DISABLED,
          borderColor: FORM_CONTROL_SURFACE_DISABLED,
          color: semantic.colour.text.textDisabled,
          cursor: 'default',
        },
        'input:checked:disabled + &': {
          color: semantic.colour.text.textDisabled,
        },
        'input[data-invalid=true] + &, input[aria-invalid=true] + &': {
          borderColor: semantic.colour.reserved.critical.background,
        },
        'input[data-invalid=true]:focus + &, input[aria-invalid=true]:focus + &': {
          boxShadow: `0 0 0 1px ${semantic.colour.reserved.critical.background}`,
        },
      }}
      {...props}
    />
  );
};

// "Dot" SVG
// ------------------------------

const sizeMap = {
  small: 16,
  medium: 24,
  large: 32,
};
const DotIcon = ({ size = 'medium' }: { size?: keyof typeof sizeMap }) => {
  let resolvedSize = sizeMap[size];
  return (
    <svg
      fill="currentColor"
      height={resolvedSize}
      viewBox="0 0 24 24"
      width={resolvedSize}
    >
      <circle cx="12" cy="12" r="8" />
    </svg>
  );
};
